// src/pages/ProfilePage.tsx
import { useEffect, useState } from "react";
import { useSupabase } from "../hooks/useSupabase";
import { getMyProfile, updateProfile } from "../services/users";
import { toast } from "react-hot-toast";
import { FiSettings, FiCalendar, FiCheck, FiX, FiUser, FiBriefcase, FiPhone, FiMapPin, FiFileText, FiGlobe } from "react-icons/fi";
import { googleCalendarService } from "../services/googleCalendar";
import GlobalHeader from "../components/layout/GlobalHeader";
import GlobalSidebar from "../components/layout/GlobalSidebar";
import "./ProfilePage.css";

export default function ProfilePage() {
  const { user } = useSupabase();
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [bio, setBio] = useState("");
  const [website, setWebsite] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Estados para Google Calendar
  const [googleCalendarEnabled, setGoogleCalendarEnabled] = useState(false);
  const [googleCalendars, setGoogleCalendars] = useState<any[]>([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState<string>("");
  const [connectingGoogle, setConnectingGoogle] = useState(false);

  useEffect(() => {
    document.title = "TaskManager - Perfil";
    if (!user) return;
    (async () => {
      const { data } = await getMyProfile(user.id);
      if (data) {
        setName(data.full_name || "");
        setAvatar(data.avatar_url || "");
        setJobTitle(data.job_title || "");
        setCompany(data.company || "");
        setPhone(data.phone || "");
        setLocation(data.location || "");
        setBio(data.bio || "");
        setWebsite(data.website || "");
      }
    })();
    
    // Verificar si Google Calendar está conectado
    checkGoogleCalendarConnection();
  }, [user]);

  const checkGoogleCalendarConnection = async () => {
    try {
      const success = await googleCalendarService.initializeWithSupabase();
      if (success) {
        setGoogleCalendarEnabled(true);
        await loadGoogleCalendars();
      }
    } catch (error) {
      console.error('Error checking Google Calendar connection:', error);
    }
  };

  const loadGoogleCalendars = async () => {
    try {
      const calendars = await googleCalendarService.getCalendarList();
      setGoogleCalendars(calendars);
      
      // Cargar calendario seleccionado previamente si existe
      // TODO: Implementar almacenamiento de preferencias en BD
    } catch (error) {
      console.error('Error loading Google calendars:', error);
    }
  };

  const handleConnectGoogle = async () => {
    setConnectingGoogle(true);
    try {
      const success = await googleCalendarService.initialize();
      if (success) {
        setGoogleCalendarEnabled(true);
        await loadGoogleCalendars();
        toast.success("Google Calendar conectado exitosamente");
      } else {
        toast.error("No se pudo conectar con Google Calendar");
      }
    } catch (error) {
      console.error('Error connecting to Google Calendar:', error);
      toast.error("Error al conectar con Google Calendar");
    } finally {
      setConnectingGoogle(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      await googleCalendarService.disconnect();
      setGoogleCalendarEnabled(false);
      setGoogleCalendars([]);
      setSelectedCalendarId("");
      toast.success("Google Calendar desconectado");
    } catch (error) {
      console.error('Error disconnecting Google Calendar:', error);
      toast.error("Error al desconectar Google Calendar");
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    const { error } = await updateProfile(user.id, {
      full_name: name.trim() || null,
      avatar_url: avatar.trim() || null,
      job_title: jobTitle.trim() || null,
      company: company.trim() || null,
      phone: phone.trim() || null,
      location: location.trim() || null,
      bio: bio.trim() || null,
      website: website.trim() || null,
    });
    setSaving(false);
    if (error) toast.error("No se pudo actualizar");
    else toast.success("Perfil actualizado");
  };

  if (!user) return <div className="panel pad-lg">Debes iniciar sesión.</div>;

  return (
    <>
      {/* Global Header */}
      <GlobalHeader
        viewType="dashboard"
        onUserClick={() => {}}
      />
      
      {/* Global Sidebar */}
      <GlobalSidebar
        projects={[]}
        companies={[]}
        tasks={[]}
        showFilters={false}
        showProjectStats={false}
        onProjectChange={() => {}}
        onCompanyChange={() => {}}
        onPriorityChange={() => {}}
        onAssigneeChange={() => {}}
        onSearchChange={() => {}}
        onClearFilters={() => {}}
      />

      <div className="jira-kanban-container">
        <div className="jira-main">
          <div className="panel pad-lg" style={{ maxWidth: 600, margin: "0 auto" }}>
            <h1 className="title">Mi perfil</h1>
            
            {/* Información personal */}
            <div className="profile-section">
              <h2 className="subtitle">
                <FiSettings size={20} />
                Información Personal
              </h2>
              <form className="vstack gap-sm" onSubmit={save}>
                <div className="profile-form-grid">
                  <label className="vstack gap-xs">
                    <span>
                      <FiUser size={16} />
                      Nombre completo *
                    </span>
                    <input
                      className="input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={saving}
                      placeholder="Tu nombre completo"
                      required
                    />
                  </label>
                  
                  <label className="vstack gap-xs">
                    <span>
                      <FiBriefcase size={16} />
                      Cargo/Puesto
                    </span>
                    <input
                      className="input"
                      value={jobTitle}
                      onChange={(e) => setJobTitle(e.target.value)}
                      disabled={saving}
                      placeholder="Ej: Desarrollador Frontend, Project Manager"
                    />
                  </label>
                  
                  <label className="vstack gap-xs">
                    <span>
                      <FiBriefcase size={16} />
                      Empresa
                    </span>
                    <input
                      className="input"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      disabled={saving}
                      placeholder="Nombre de tu empresa"
                    />
                  </label>
                  
                  <label className="vstack gap-xs">
                    <span>
                      <FiPhone size={16} />
                      Teléfono
                    </span>
                    <input
                      className="input"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      disabled={saving}
                      placeholder="+34 123 456 789"
                    />
                  </label>
                  
                  <label className="vstack gap-xs">
                    <span>
                      <FiMapPin size={16} />
                      Ubicación
                    </span>
                    <input
                      className="input"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      disabled={saving}
                      placeholder="Ciudad, País"
                    />
                  </label>
                  
                  <label className="vstack gap-xs">
                    <span>
                      <FiGlobe size={16} />
                      Sitio web
                    </span>
                    <input
                      className="input"
                      type="url"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      disabled={saving}
                      placeholder="https://tu-sitio-web.com"
                    />
                  </label>
                </div>
                
                <label className="vstack gap-xs">
                  <span>
                    <FiSettings size={16} />
                    Avatar URL
                  </span>
                  <input
                    className="input"
                    type="url"
                    value={avatar}
                    onChange={(e) => setAvatar(e.target.value)}
                    disabled={saving}
                    placeholder="https://ejemplo.com/tu-foto.jpg"
                  />
                  {avatar && (
                    <div className="avatar-preview">
                      <img 
                        src={avatar} 
                        alt="Vista previa del avatar" 
                        style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </label>
                
                <label className="vstack gap-xs">
                  <span>
                    <FiFileText size={16} />
                    Biografía
                  </span>
                  <textarea
                    className="input"
                    rows={4}
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    disabled={saving}
                    placeholder="Cuéntanos un poco sobre ti, tu experiencia profesional, intereses..."
                    style={{ resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </label>
                
                <button className="btn btn-primary" type="submit" disabled={saving}>
                  {saving ? "Guardando…" : "Guardar Perfil"}
                </button>
              </form>
            </div>

            {/* Integración con Google Calendar */}
            <div className="profile-section">
              <h2 className="subtitle">
                <FiCalendar size={20} />
                Google Calendar
              </h2>
              
              {!googleCalendarEnabled ? (
                <div className="google-calendar-setup">
                  <p className="muted">
                    Conecta tu cuenta de Google Calendar para sincronizar eventos con tus tareas.
                  </p>
                  <button 
                    className="btn btn-primary"
                    onClick={handleConnectGoogle}
                    disabled={connectingGoogle}
                  >
                    {connectingGoogle ? "Conectando..." : "Conectar Google Calendar"}
                  </button>
                </div>
              ) : (
                <div className="google-calendar-connected">
                  <div className="connection-status">
                    <FiCheck size={16} color="#4CAF50" />
                    <span>Google Calendar conectado</span>
                  </div>
                  
                  {googleCalendars.length > 0 && (
                    <div className="calendar-selector">
                      <label className="vstack gap-xs">
                        <span>Calendario para sincronización:</span>
                        <select
                          className="input"
                          value={selectedCalendarId}
                          onChange={(e) => setSelectedCalendarId(e.target.value)}
                        >
                          <option value="">Seleccionar calendario</option>
                          {googleCalendars.map((calendar) => (
                            <option key={calendar.id} value={calendar.id}>
                              {calendar.summary}
                            </option>
                          ))}
                        </select>
                      </label>
                      
                      {selectedCalendarId && (
                        <div className="sync-info">
                          <p className="muted small">
                            Las tareas con fechas se sincronizarán automáticamente con el calendario seleccionado.
                            Los cambios en cualquiera de las dos plataformas se reflejarán en la otra.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <button 
                    className="btn btn-secondary"
                    onClick={handleDisconnectGoogle}
                  >
                    <FiX size={16} />
                    Desconectar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
